import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, FlaskConical, HeartPulse, Dumbbell } from "lucide-react";
import { api, HERO_BG } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";

const trust = [
  { icon: ShieldCheck, title: "≥ 99% Purity", desc: "Third-party verified, high-purity compounds." },
  { icon: Truck, title: "Fast U.S. Shipping", desc: "Domestic origin, quick dispatch nationwide." },
  { icon: FlaskConical, title: "Domestic Sourced", desc: "Sourced and handled entirely within the U.S." },
];

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data.filter((p) => p.best_seller).slice(0, 4)));
  }, []);

  return (
    <>
      <Seo
        title="Just Peptides Supply | Research Peptides and Laboratory Supplies"
        description="High-purity, domestic-origin research peptides with fast U.S. shipping, telehealth intake consultations, and physical training coaching."
        path="/"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-brand-ink text-white">
        <img src={HERO_BG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-ink via-brand-ink/85 to-transparent" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-32 grid lg:grid-cols-2 gap-12 items-center">
          <div className="max-w-2xl fade-up">
            <p className="font-mono-label text-xs uppercase tracking-[0.25em] text-brand-teal">Just Peptides · Total Performance</p>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              High-purity peptides,<br /> <span className="brand-gradient-text">engineered for results.</span>
            </h1>
            <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-xl">
              Fast U.S. shipping and domestic-origin research peptides for precision performance — paired with
              telehealth intake and physical training coaching.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/shop">
                <Button data-testid="hero-shop-button" className="rounded-full bg-brand-teal hover:bg-brand-blue text-white h-12 px-7 text-base">
                  Shop Peptides <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/telehealth">
                <Button data-testid="hero-consult-button" variant="outline" className="rounded-full border-white/30 bg-white/5 text-white hover:bg-white/15 h-12 px-7 text-base">
                  Book a Consult
                </Button>
              </Link>
            </div>
          </div>

          <div className="hidden lg:block relative fade-up" style={{ animationDelay: "150ms" }}>
            <div className="relative mx-auto w-[380px]">
              <div className="rounded-3xl overflow-hidden border border-white/15 shadow-2xl bg-white">
                <img src="https://customer-assets.emergentagent.com/job_just-peptides-shop/artifacts/mtn4zgr4_image.png" alt="BPC-157 research peptide vial" className="w-full aspect-square object-cover" />
              </div>
              <div className="absolute -left-8 top-10 glass rounded-2xl px-5 py-4 shadow-xl border border-white/40">
                <p className="font-mono-label text-[10px] uppercase tracking-[0.15em] text-brand-navy/50">Purity</p>
                <p className="font-display text-2xl text-brand-navy">≥ 99%</p>
              </div>
              <div className="absolute -right-6 bottom-12 glass rounded-2xl px-5 py-4 shadow-xl border border-white/40">
                <p className="font-mono-label text-[10px] uppercase tracking-[0.15em] text-brand-navy/50">Origin</p>
                <p className="font-display text-2xl text-brand-navy">Domestic</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-b border-brand-navy/10 bg-brand-mist">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 py-10 grid gap-8 sm:grid-cols-3">
          {trust.map((t) => (
            <div key={t.title} className="flex items-start gap-4">
              <div className="rounded-xl bg-white border border-brand-navy/10 p-3">
                <t.icon className="h-6 w-6 text-brand-blue" />
              </div>
              <div>
                <p className="font-display text-lg text-brand-navy">{t.title}</p>
                <p className="text-sm text-brand-navy/55">{t.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Best sellers */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="font-mono-label text-xs uppercase tracking-[0.2em] text-brand-blue">Best Sellers</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl text-brand-navy tracking-tight">Precision compounds</h2>
          </div>
          <Link to="/shop" className="hidden sm:flex items-center gap-2 font-mono-label text-xs uppercase tracking-[0.15em] text-brand-navy/70 hover:text-brand-blue transition-colors">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {products.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* Services split */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-8">
        <div className="grid gap-6 md:grid-cols-2">
          <ServiceCard
            to="/telehealth"
            icon={HeartPulse}
            tag="Telehealth"
            title="Peptide Intake Consultations"
            desc="Work one-on-one with a clinician to build a research-informed protocol tailored to your goals."
            img="https://images.unsplash.com/photo-1758691463606-1493d79cc577?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          />
          <ServiceCard
            to="/training"
            icon={Dumbbell}
            tag="Coaching"
            title="Physical Training Programs"
            desc="Structured strength and conditioning coaching designed to complement your performance journey."
            img="https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          />
        </div>
      </section>
    </>
  );
}

const ServiceCard = ({ to, icon: Icon, tag, title, desc, img }) => (
  <Link to={to} data-testid={`service-${tag.toLowerCase()}`} className="group relative overflow-hidden rounded-2xl bg-brand-ink text-white min-h-[320px] flex">
    <img src={img} alt={title} className="absolute inset-0 h-full w-full object-cover opacity-45 group-hover:opacity-55 group-hover:scale-105 transition-[opacity,transform] duration-500" />
    <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/60 to-transparent" />
    <div className="relative mt-auto p-8">
      <div className="inline-flex items-center gap-2 font-mono-label text-[11px] uppercase tracking-[0.2em] text-brand-teal">
        <Icon className="h-4 w-4" /> {tag}
      </div>
      <h3 className="mt-3 font-display text-2xl">{title}</h3>
      <p className="mt-2 text-sm text-white/70 max-w-md">{desc}</p>
      <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-white group-hover:gap-3 transition-all">
        Learn more <ArrowRight className="h-4 w-4" />
      </span>
    </div>
  </Link>
);
